import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { ConversationViewContainer } from '@/features/messaging/components/ConversationView/ConversationViewContainer'
import { ConversationHeaderBanner } from '@/features/messaging/components/ConversationView/ConversationHeaderBanner'
import { ConversationHeader } from '@/features/messaging/components/ConversationView/ConversationHeader'
import { ConversationComposerAddMenu } from '@/features/messaging/components/ConversationView/ConversationComposer'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { dateFormatters } from '@/utils/date-util'
import {
  ChatDisabled,
  NoConversation,
  RevealJobNotesButton,
} from '@/features/jobs/details/components/CommunicationView'

export interface JobConversationContainerProps {
  vm: JobDetailsViewModel
  showDocumentTabOnUpload: boolean
  /**
   * For hosts that show who the chat is with and its actions themselves.
   */
  hideHeader?: boolean
}

export const JobConversationContainer = observer(
  function JobConversationContainer({
    vm,
    showDocumentTabOnUpload,
    hideHeader,
  }: JobConversationContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    return vm.canViewChat ? (
      vm.job.conversation === null ? (
        <NoConversation corporateMode={shellInfo.corporateMode} />
      ) : (
        <ConversationViewContainer
          vm={vm.conversationVm}
          header={!hideHeader && <JobConversationHeaderContainer vm={vm} />}
          banner={<OpenTicketConversationBannerContainer vm={vm} />}
          readOnly={vm.isChatReadOnly}
          welcomeActions={
            vm.jobConversationVm.canEnableNotifications
              ? [
                  {
                    onPress: vm.jobConversationVm.enableNotifications,
                    label: 'Turn on Notifications',
                  },
                ]
              : []
          }
          composerLeadingActions={
            vm.canUploadDocs && (
              <JobConversationAddMenuContainer
                vm={vm}
                showDocumentTabOnUpload={showDocumentTabOnUpload}
              />
            )
          }
          composerTrailingActions={
            vm.canViewJobNotes && (
              <RevealJobNotesButton onPress={vm.revealNotesFromChat} />
            )
          }
        />
      )
    ) : (
      <ChatDisabled corporateMode={shellInfo.corporateMode} />
    )
  },
)

interface JobConversationHeaderContainerProps {
  vm: JobDetailsViewModel
}

const JobConversationHeaderContainer = observer(
  function JobConversationHeaderContainer({
    vm,
  }: JobConversationHeaderContainerProps) {
    return (
      <ConversationHeader
        online={vm.isChatClientOnline}
        avatar={vm.chatAvatarMember ?? undefined}
        displayName={vm.chatDisplayName}
        showLastSeen={vm.canViewLastSeenInfoInChat}
        lastSeenText={vm.chatLastSeenText}
        canRefresh={vm.conversationVm.canRefreshManually}
        onRefresh={vm.conversationVm.fetchLatestMessages}
        muted={vm.conversationVm.muted}
        onToggleMute={vm.conversationVm.toggleMute}
        canDownload={vm.canDownloadConversation}
        onDownload={vm.conversationVm.downloadConversation}
      />
    )
  },
)

interface JobConversationAddMenuContainerProps {
  vm: JobDetailsViewModel
  showDocumentTabOnUpload: boolean
}

const JobConversationAddMenuContainer = observer(
  function JobConversationAddMenuContainer({
    vm,
    showDocumentTabOnUpload,
  }: JobConversationAddMenuContainerProps) {
    return (
      <ConversationComposerAddMenu
        documentTypes={vm.chatDocumentTypes}
        onUpload={vm.stageChatDocuments}
        canUploadOtherDocuments={vm.canUploadOtherChatDocuments}
        onUploadOtherDocuments={vm.stageOtherChatDocuments}
        showDocumentTab={showDocumentTabOnUpload ? vm.showDocumentsTab : noop}
        onLinkExisting={vm.documentLinkingPickerVm.show}
        showMessageBank={vm.conversationVm.shouldShowMessageBank}
        onMessageBank={vm.messageBankDialogVm.show}
      />
    )
  },
)

interface OpenTicketConversationBannerContainerProps {
  vm: JobDetailsViewModel
}

const OpenTicketConversationBannerContainer = observer(
  function OpenTicketConversationBannerContainer({
    vm,
  }: OpenTicketConversationBannerContainerProps) {
    const ticket = vm.jobTicketsVm.latestTicket
    if (!ticket) {
      return null
    }

    const title = (
      <>
        Open ticket, last update{' '}
        {dateFormatters.dateTimeDefault.format(ticket.lastUpdated)}
      </>
    )

    return (
      <ConversationHeaderBanner title={title}>
        {ticket.statusMessage}
      </ConversationHeaderBanner>
    )
  },
)

const noop = () => undefined
